"use client";

import React from "react";
import { InfoText } from "@/components/InfoText";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";
import type { DispatchRecipient, NoneOffered } from "@/utils/dispatchControl";

type DispatchValue = DispatchRecipient;

interface DispatchControlSectionProps {
  dispatchContract: DispatchValue;
  dispatchInvoice: DispatchValue;
  dispatchEmail: DispatchValue;
  agentEmails: string[];
  agentDispatchEmail: string;
  onDispatchContractChange: (v: DispatchValue) => void;
  onDispatchInvoiceChange: (v: DispatchValue) => void;
  onDispatchEmailChange: (v: DispatchValue) => void;
  onAgentDispatchEmailChange: (email: string) => void;
  agentDispatchEmailError: string;
  agentDispatchEmailRef: React.RefObject<HTMLDivElement>;
  /** Categories the offer had set to "none": only they offer "keine". */
  noneOffered?: NoneOffered;
}

/** One row of the section: label (with optional info icon) on the left, field on the right. */
const Row: React.FC<{ htmlFor: string; label: string; info?: React.ReactNode; rowRef?: React.RefObject<HTMLDivElement>; children: React.ReactNode }> = ({
  htmlFor,
  label,
  info,
  rowRef,
  children,
}) => (
  <div ref={rowRef} className="grid grid-cols-1 items-center gap-x-6 gap-y-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
    <div className="flex items-center gap-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {info}
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const DispatchControlSection: React.FC<DispatchControlSectionProps> = ({
  dispatchContract,
  dispatchInvoice,
  dispatchEmail,
  agentEmails,
  agentDispatchEmail,
  onDispatchContractChange,
  onDispatchInvoiceChange,
  onDispatchEmailChange,
  onAgentDispatchEmailChange,
  agentDispatchEmailError,
  agentDispatchEmailRef,
  noneOffered,
}) => {
  const { t } = useTranslation();
  const dispatchOptionLabel = (value: DispatchValue) => {
    if (value === "customer") return t("broker-display-customer");
    if (value === "customer_and_agent") return t("broker-display-customer-and-agent");
    if (value === "none") return t("broker-display-none");
    return t("broker-display-agent");
  };

  const dispatchSelect = (id: string, value: DispatchValue, onChange: (v: DispatchValue) => void, withNone: boolean) => {
    const dispatchOptions: DispatchValue[] = withNone ? ["customer", "customer_and_agent", "agent", "none"] : ["customer", "customer_and_agent", "agent"];
    return (
    <Select value={value} onValueChange={(v) => onChange(v as DispatchValue)}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={t("contactDetails.selectDispatchPlaceholder")} />
      </SelectTrigger>
      <SelectContent>
        {dispatchOptions.map((opt) => (
          <SelectItem key={`${id}-${opt}`} value={opt}>
            {dispatchOptionLabel(opt)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    );
  };

  /** Info icon that opens a dialog with the field's explanation (texts as in the broker back office funnel). */
  const infoTip = (title: string, text: string) => (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
          <Info className="h-5! w-5! text-muted-foreground" />
          <span className="sr-only">{`${t("common.info")}: ${title}`}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <InfoText text={text} />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );

  // Reminders (Erinnerungen) have no field of their own: they follow the Inkasso setting (see ContactDetailsForm).
  return (
    <section className="space-y-4">
      <h3 className="text-xl font-semibold">{t("contactDetails.dispatchControlTitle")}</h3>
      {agentEmails && agentEmails.length > 0 && (
        <Row htmlFor="agentEmail" label={t("contactDetails.agentEmailLabel")} rowRef={agentDispatchEmailRef}>
          <Select value={agentDispatchEmail || ""} onValueChange={onAgentDispatchEmailChange}>
            <SelectTrigger id="agentEmail" className={cn(agentDispatchEmailError && "border-destructive focus-visible:ring-destructive")}>
              <SelectValue placeholder={t("contactDetails.chooseAgentEmailPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {agentEmails.map((e) => (
                <SelectItem key={e} value={e}>{e}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {agentDispatchEmailError && <p className="text-sm text-destructive">{agentDispatchEmailError}</p>}
        </Row>
      )}
      <Row htmlFor="dispatchContract" label={t("contactDetails.dispatchContracts")} info={infoTip(t("contactDetails.dispatchContracts"), t("contactDetails.dispatchContractsInfo"))}>
        {dispatchSelect("dispatchContract", dispatchContract, onDispatchContractChange, !!noneOffered?.contract)}
      </Row>
      <Row htmlFor="dispatchInvoice" label={t("contactDetails.dispatchInvoices")} info={infoTip(t("contactDetails.dispatchInvoices"), t("contactDetails.dispatchInvoicesInfo"))}>
        {dispatchSelect("dispatchInvoice", dispatchInvoice, onDispatchInvoiceChange, !!noneOffered?.invoice)}
      </Row>
      <Row htmlFor="dispatchEmail" label={t("contactDetails.dispatchEmails")} info={infoTip(t("contactDetails.dispatchEmails"), t("contactDetails.dispatchEmailsInfo"))}>
        {dispatchSelect("dispatchEmail", dispatchEmail, onDispatchEmailChange, !!noneOffered?.email)}
      </Row>
    </section>
  );
};

export default DispatchControlSection;
